import { useEffect } from "react";
import { Routes, Route, Navigate } from "react-router-dom";
import { SWRConfig } from "swr";
import { useAuthStore } from "@/store/auth";
import { LoginPage } from "@/pages/LoginPage";
import { StudioPage } from "@/pages/StudioPage";
import { GalleryPage } from "@/pages/GalleryPage";
import { LibraryPage } from "@/pages/LibraryPage";
import { ProjectsPage } from "@/pages/ProjectsPage";
import { ProductionAssetsPage } from "@/pages/ProductionAssetsPage";
import { AdminPage } from "@/pages/AdminPage";
import { SettingsPage } from "@/pages/SettingsPage";
import { GeneralSettingsPage } from "@/pages/settings/GeneralSettingsPage";
import { AppearanceSettingsPage } from "@/pages/settings/AppearanceSettingsPage";
import { TeamAccessSettingsPage } from "@/pages/settings/TeamAccessSettingsPage";
import { ActivatePage } from "@/pages/ActivatePage";
import { ChangePasswordPage } from "@/pages/ChangePasswordPage";
import { NoActiveTeamPage } from "@/pages/NoActiveTeamPage";
import { StudioLayout } from "@/components/StudioLayout";
import { ThemeProvider } from "@/components/theme/ThemeProvider";

const createSessionCache = () => new Map();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const status = useAuthStore((s) => s.status);
  const activeTeam = useAuthStore((s) => s.activeTeam);
  const isSuperuser = useAuthStore((s) => s.user?.is_superuser);
  if (status === "checking") return <SessionLoading />;
  if (status !== "authenticated") return <Navigate to="/login" replace />;
  if (!activeTeam && !isSuperuser) return <NoActiveTeamPage />;
  return <>{children}</>;
}

function LoginRoute() {
  const status = useAuthStore((s) => s.status);
  if (status === "checking") return <SessionLoading />;
  if (status === "authenticated") return <Navigate to="/" replace />;
  return <LoginPage />;
}

function SessionLoading() {
  return (
    <div className="mobile-safe-top mobile-safe-bottom flex min-h-dvh items-center justify-center text-sm text-studio-muted">
      Checking session…
    </div>
  );
}

export default function App() {
  const initialize = useAuthStore((state) => state.initialize);
  const cacheScope = useAuthStore((state) =>
    `${state.user?.id ?? "guest"}:${state.activeTeam?.id ?? "none"}:${state.activeTeam?.role ?? "none"}:${state.user?.is_superuser ?? false}`
  );

  useEffect(() => {
    void initialize();
  }, [initialize]);

  return (
    <SWRConfig key={cacheScope} value={{ provider: createSessionCache }}>
      <Routes>
        <Route path="/login" element={<LoginRoute />} />
        <Route path="/activate" element={<ActivatePage />} />
        <Route path="/change-password" element={<ChangePasswordPage />} />
        <Route
          element={
            <ProtectedRoute>
              <ThemeProvider>
                <StudioLayout />
              </ThemeProvider>
            </ProtectedRoute>
          }
        >
          <Route path="/" element={<StudioPage />} />
          <Route path="/gallery" element={<GalleryPage />} />
          <Route path="/library" element={<LibraryPage />} />
          <Route path="/elements" element={<ProductionAssetsPage />} />
          <Route path="/projects" element={<ProjectsPage />} />
          <Route path="/settings" element={<SettingsPage />}>
            <Route index element={<Navigate to="general" replace />} />
            <Route path="general" element={<GeneralSettingsPage />} />
            <Route path="appearance" element={<AppearanceSettingsPage />} />
            <Route path="team-access" element={<TeamAccessSettingsPage />} />
          </Route>
          <Route path="/admin" element={<AdminPage />} />
        </Route>
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </SWRConfig>
  );
}
